// src/router/index.ts
import { createRouter, createWebHistory, RouteRecordRaw } from "vue-router";

// ⚠️ Gardons HomeView + LangueWrapper en import statique
// pour un TTI rapide côté public (page d’accueil).
import HomeView from "../views/Visitor/HomeView.vue";
import LangueWrapper from "../components/Visitor_Component/Shared/LangueWrapper.vue";
// 404 peut rester statique (faible coût, utile partout)
import Page404 from "../views/Page404.vue";
import PrivacyPolicy from '../views/PrivacyPolicy.vue'; // Déjà importé ici, parfait.
import store from "@/store"; 


// ------------------------------------------------------
// Auth helpers
// ------------------------------------------------------
const TOKEN_KEY = "auth_token";

function getToken() {
  return localStorage.getItem(TOKEN_KEY) || "";
}
function parseJwt(token: string): any | null {
  try {
    const base = token.split(".")[1];
    return JSON.parse(
      decodeURIComponent(
        atob(base)
          .split("")
          .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
          .join("")
      )
    );
  } catch {
    return null;
  }
}
function isJwtExpired(token: string) {
  const payload = parseJwt(token);
  if (!payload?.exp) return true;
  return Date.now() >= payload.exp * 1000;
}
function isAuthenticated() {
  const tok = getToken();
  if (!tok) return false;
  if (isJwtExpired(tok)) {
    localStorage.removeItem(TOKEN_KEY);
    return false;
  }
  return true;
}

// ------------------------------------------------------
// Lazy imports (code-splitting) — Admin & autres vues
// ------------------------------------------------------
const AdminLayout = () =>
  import(/* webpackChunkName: "admin-layout" */ "../components/Admin_Component/AdminLayout.vue");

const AdminTestimonials = () =>
  import(/* webpackChunkName: "admin-testimonials" */ "../views/Admin/TestimonialsView.vue");

const AdminServices = () =>
  import(/* webpackChunkName: "admin-testimonials" */ "../views/Admin/ServiceManagementView.vue");

const AdminManageContent = () =>
  import(/* webpackChunkName: "admin-manage" */ "../views/Admin/ManageContentView.vue");

const AdminContactInbox = () =>
  import(/* webpackChunkName: "admin-inbox" */ "../views/Admin/AdminContactInbox.vue");

// Les vues “Visitor” utilisées *dans* l’admin (onglets d’édition) restent lazy
const VisitorAbout = () =>
  import(/* webpackChunkName: "visitor-about" */ "../views/Visitor/AboutView.vue");
const VisitorPortfolio = () =>
  import(/* webpackChunkName: "visitor-portfolio" */ "../views/Visitor/PortfolioView.vue");
const VisitorContact = () =>
  import(/* webpackChunkName: "visitor-contact" */ "../views/Visitor/ContactView.vue");

// Vue détail Service (visitor)
const ServiceDetailView = () =>
  import(/* webpackChunkName: "visitor-service-detail" */ "../views/Visitor/ServiceDetailView.vue");

const ServiceByTagView = () =>
  import(/* webpackChunkName: "visitor-service-by-tag" */ "../views/Visitor/ServiceByTagView.vue");
// Auth
const LoginView = () =>
  import(/* webpackChunkName: "auth" */ "../views/Login.vue");

// ------------------------------------------------------
// Routes
// ------------------------------------------------------
const routes: Array<RouteRecordRaw> = [
  // Admin
  {
    path: "/admin",
    component: AdminLayout,
    meta: { requiresAuth: true, title: "Admin" },
    children: [
      {
        path: "testimonials",
        name: "testimonials",
        component: AdminTestimonials,
        meta: { title: "Testimonials", requiresAuth: true },
      },
      {
        path: "services",
        name: "services",
        component: AdminServices,
        meta: { title: "services", requiresAuth: true },
      },
      {
        path: "",
        name: "manage-content",
        component: AdminManageContent,
        meta: { title: "Manage Content", requiresAuth: true },
        children: [
          {
            path: "home",
            name: "admin-home",
            // On réutilise la vue publique pour l’édition
            component: () =>
              import(/* webpackChunkName: "visitor-home" */ "../views/Visitor/HomeView.vue"),
            meta: { title: "Manage Home", requiresAuth: true },
          },
          {
            path: "about",
            name: "admin-about",
            component: VisitorAbout,
            meta: { title: "Manage About", requiresAuth: true },
          },
          {
            path: "portfolio",
            name: "admin-portfolio",
            component: VisitorPortfolio,
            meta: { title: "Manage Portfolio", requiresAuth: true },
          },
          {
            path: "contact",
            name: "admin-contact",
            component: VisitorContact,
            meta: { title: "Manage Contact", requiresAuth: true },
          },
        ],
      },
      {
        path: "AdminContactInbox",
        name: "AdminContactInbox",
        component: AdminContactInbox,
        meta: { title: "AdminContactInbox", requiresAuth: true },
      },
    ],
  },

  // Auth
  {
    path: "/login",
    name: "login",
    component: LoginView,
    meta: { title: "Login" },
  },

  // Public
  { path: "/", redirect: "/fr" },
  { path: "/fr/accueil", redirect: "/fr" },
  { path: "/en/home", redirect: "/en" },

  {
    path: "/:langue",
    component: LangueWrapper,
    // petite validation de langue ici
    beforeEnter: (to, _from, next) => {
      const lang = String(to.params.langue);
      if (!["en", "fr"].includes(lang)) {
        return next("/fr");
      }
      next();
    },
    children: [
      {
        path: "",
        name: "home",
        component: HomeView,
        beforeEnter: (to, _from, next) => {
          const lang = to.params.langue;
          if (lang === "en") to.meta.title = "Home";
          else if (lang === "fr") to.meta.title = "Accueil";
          next();
        },
      },
      {
        path: "a-propos",
        name: "about-fr",
        component: VisitorAbout,
        meta: { title: "À Propos", langue: "fr" },
      },
      {
        path: "about",
        name: "about-en",
        component: VisitorAbout,
        meta: { title: "About", langue: "en" },
      },
      {
        path: "portfolio",
        name: "portfolio",
        component: VisitorPortfolio,
        meta: { title: "Portfolio" },
      },
      {
        path: "contact",
        name: "contact",
        component: VisitorContact,
        meta: { title: "Contact" },
      },
      {
        path: 'services/:slug',
        name: 'service-detail',
        component: ServiceDetailView,
        meta: { title: "Service" },
      },
      {
        path: 'services/tag/:tag',
        name: 'services-by-tag',
        component: ServiceByTagView,
        beforeEnter: (to, _from, next) => {
          const tag = decodeURIComponent(String(to.params.tag || ''));
          const lang = String(to.params.langue || 'en');
          const baseTitle = 'H&S Conseil';
          const pageTitle = lang === 'fr'
            ? `#${tag} — Services`
            : `#${tag} — Services`;
          document.title = `${pageTitle} - ${baseTitle}`;
          next();
        },
        meta: { title: 'Services' },
      },

      // ========================================================
      // AJOUT : Politique de confidentialité (SEO Friendly)
      // ========================================================
      {
        path: "politique-de-confidentialite",
        name: "privacy-fr",
        component: PrivacyPolicy,
        meta: { title: "Politique de confidentialité" },
      },
      {
        path: "privacy",
        name: "privacy-en",
        component: PrivacyPolicy,
        meta: { title: "Privacy Policy" },
      },
      // ========================================================

      {
        path: ":pathMatch(.*)*",
        name: "NotFound",
        component: Page404,
        meta: { title: "Page Introuvable" },
      },
    ],
  },
];

// ------------------------------------------------------
// Router instance
// ------------------------------------------------------
const router = createRouter({
  history: createWebHistory(process.env.BASE_URL), // Vue CLI
  routes,
  scrollBehavior(to, _from, savedPosition) {
    if (savedPosition) return savedPosition;
    if (to.hash) {
      return { el: to.hash, top: 0, behavior: "smooth" };
    }
    return { left: 0, top: 0 };
  },
});

// ------------------------------------------------------
// Guards globaux
// ------------------------------------------------------

router.beforeEach((to, _from, next) => {
  const lang = typeof to.params.langue === "string" ? to.params.langue : "";
  if (lang && (lang === "fr" || lang === "en")) {
    if (store.getters.getLangue !== lang) {
      store.commit("setLangue", lang);
    }
  }
  next();
});


// Auth guard (centralisé)
router.beforeEach((to, _from, next) => {
  if (to.meta?.requiresAuth && !isAuthenticated()) {
    return next("/login");
  }
  next();
});

// Title
router.beforeEach((to, _from, next) => {
  const baseTitle = "H&S Conseil";
  document.title = to.meta?.title ? `${to.meta.title} - ${baseTitle}` : baseTitle;
  next();
});

// Résilience : si un chunk lazy échoue à se charger, on force 1 reload
let hasReloadedForChunkError = false;
router.onError((err: any) => {
  const chunkFailed =
    err?.message?.includes("Loading chunk") ||
    err?.message?.includes("ChunkLoadError") ||
    /Failed to fetch dynamically imported module/.test(String(err?.message || ""));

  if (chunkFailed && !hasReloadedForChunkError) {
    hasReloadedForChunkError = true;
    window.location.reload();
  } else {
    console.warn("[router] onError:", err);
  }
});

export default router;